// ========================================
// 📄 src/utils/errorHandler.js
// ========================================

class ErrorHandler {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Limite pour éviter la surcharge mémoire
  }

  // Logger une erreur
  logError(error, context = {}) {
    const errorEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      message: error.message || "Erreur inconnue",
      stack: error.stack,
      code: error.code,
      context: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...context,
      },
    };

    this.errors.push(errorEntry);

    // Limiter le nombre d'erreurs stockées
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Log dans la console en développement
    if (process.env.NODE_ENV === "development") {
      console.group("🚨 Erreur capturée");
      console.error("Message:", errorEntry.message);
      console.error("Code:", errorEntry.code);
      console.error("Stack:", errorEntry.stack);
      console.error("Context:", errorEntry.context);
      console.groupEnd();
    }

    // En production, vous pourriez envoyer les erreurs à un service externe
    // comme Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === "production") {
      this.sendToExternalService(errorEntry);
    }

    return errorEntry.id;
  }

  // Envoyer les erreurs à un service externe (exemple)
  sendToExternalService(errorEntry) {
    // Exemple d'intégration avec un service externe
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorEntry)
    // }).catch(err => console.error('Erreur lors de l\'envoi:', err));
  }

  // Obtenir toutes les erreurs
  getAllErrors() {
    return [...this.errors];
  }

  // Obtenir les erreurs récentes
  getRecentErrors(limit = 10) {
    return this.errors.slice(-limit);
  }

  // Supprimer toutes les erreurs
  clearErrors() {
    this.errors = [];
  }

  // Créer une erreur personnalisée
  createError(message, code = null, context = {}) {
    const error = new Error(message);
    if (code) error.code = code;
    return this.logError(error, context);
  }

  // Gestionnaire d'erreur global pour les promesses non capturées
  handleUnhandledRejection(event) {
    this.logError(event.reason, {
      type: "unhandledRejection",
      userAction: "unknown",
    });
  }

  // Gestionnaire d'erreur global pour les erreurs JavaScript
  handleGlobalError(event) {
    this.logError(event.error, {
      type: "globalError",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  }

  // Initialiser les gestionnaires d'erreur globaux
  init() {
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection.bind(this)
    );
    window.addEventListener("error", this.handleGlobalError.bind(this));
  }

  // Nettoyer les gestionnaires d'erreur
  cleanup() {
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection.bind(this)
    );
    window.removeEventListener("error", this.handleGlobalError.bind(this));
  }

  // Wrapper pour les fonctions async
  async wrapAsync(fn, context = {}) {
    try {
      return await fn();
    } catch (error) {
      this.logError(error, context);
      throw error;
    }
  }

  // Wrapper pour les fonctions synchrones
  wrapSync(fn, context = {}) {
    try {
      return fn();
    } catch (error) {
      this.logError(error, context);
      throw error;
    }
  }

  // Obtenir un message d'erreur utilisateur-friendly
  getUserFriendlyMessage(error) {
    const errorMessages = {
      "auth/user-not-found": "Utilisateur non trouvé",
      "auth/wrong-password": "Mot de passe incorrect",
      "auth/email-already-in-use": "Cet email est déjà utilisé",
      "auth/weak-password":
        "Le mot de passe doit contenir au moins 6 caractères",
      "auth/invalid-email": "Adresse email invalide",
      "auth/too-many-requests": "Trop de tentatives. Réessayez plus tard",
      "permission-denied": "Permission refusée",
      unavailable: "Service temporairement indisponible",
      "network-error": "Problème de connexion",
      "validation-error": "Données invalides",
    };

    return (
      errorMessages[error.code] ||
      error.message ||
      "Une erreur inattendue s'est produite"
    );
  }

  // Exporter les erreurs pour le debugging
  exportErrors() {
    const exportData = {
      timestamp: new Date().toISOString(),
      errors: this.errors,
      summary: {
        total: this.errors.length,
        byType: this.getErrorsByType(),
        recent: this.getRecentErrors(5),
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `errors-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Obtenir les erreurs groupées par type
  getErrorsByType() {
    const types = {};
    this.errors.forEach((error) => {
      const type = error.context?.type || "unknown";
      types[type] = (types[type] || 0) + 1;
    });
    return types;
  }
}

// Instance singleton
export const errorHandler = new ErrorHandler();

// Initialiser automatiquement
errorHandler.init();

// Hook React pour utiliser le gestionnaire d'erreur
export const useErrorHandler = () => {
  return {
    logError: errorHandler.logError.bind(errorHandler),
    createError: errorHandler.createError.bind(errorHandler),
    getUserFriendlyMessage:
      errorHandler.getUserFriendlyMessage.bind(errorHandler),
    wrapAsync: errorHandler.wrapAsync.bind(errorHandler),
    wrapSync: errorHandler.wrapSync.bind(errorHandler),
    getAllErrors: errorHandler.getAllErrors.bind(errorHandler),
    getRecentErrors: errorHandler.getRecentErrors.bind(errorHandler),
    clearErrors: errorHandler.clearErrors.bind(errorHandler),
    exportErrors: errorHandler.exportErrors.bind(errorHandler),
  };
};
